// WIDGET_READY / INIT postMessage handshake between this widget and its host,
// plus the cart-operation postMessage channel added for #113540 (ADD_TO_CART,
// REMOVE_SEAT, PATRON_HOLD_LOAD and the inbound SEAT_DESELECTED notification).
//
// No transport abstraction is used here (ChannelClient was cancelled for this
// work item) - this module talks directly to window.postMessage /
// window.addEventListener('message', ...).
//
// Note: this repo has no FEATURE_FLAGS postMessage channel to remove/replace.
// See docs/postmessage-catalog.md for the full message catalog.

export const MESSAGE_TYPES = {
	WIDGET_READY: 'WIDGET_READY',
	INIT: 'INIT',
	ADD_TO_CART: 'ADD_TO_CART',
	REMOVE_SEAT: 'REMOVE_SEAT',
	PATRON_HOLD_LOAD: 'PATRON_HOLD_LOAD',
	ADD_TO_CART_RESULT: 'ADD_TO_CART_RESULT',
	REMOVE_SEAT_RESULT: 'REMOVE_SEAT_RESULT',
	PATRON_HOLD_LOAD_RESULT: 'PATRON_HOLD_LOAD_RESULT',
	ADD_TO_CART_ERROR: 'ADD_TO_CART_ERROR',
	REMOVE_SEAT_ERROR: 'REMOVE_SEAT_ERROR',
	PATRON_HOLD_LOAD_ERROR: 'PATRON_HOLD_LOAD_ERROR',
	SEAT_DESELECTED: 'SEAT_DESELECTED',
};

const CART_RESULT_TYPES = [
	MESSAGE_TYPES.ADD_TO_CART_RESULT,
	MESSAGE_TYPES.REMOVE_SEAT_RESULT,
	MESSAGE_TYPES.PATRON_HOLD_LOAD_RESULT,
];

// The host answers a request it failed to fulfil with one of these, letting the request fail straight away
// instead of waiting out the timeout below.
const CART_ERROR_TYPES = [
	MESSAGE_TYPES.ADD_TO_CART_ERROR,
	MESSAGE_TYPES.REMOVE_SEAT_ERROR,
	MESSAGE_TYPES.PATRON_HOLD_LOAD_ERROR,
];

// Requests are posted as soon as they are made and the host's runSerialized
// queue (#113540) works through them in arrival order, folding consecutive
// ADD_TO_CARTs into one cart call. A request sent while others are still
// outstanding can wait behind them, so its timeout grows with how many this
// widget already has in flight (#117787, #117935).
const CART_REQUEST_BASE_TIMEOUT_MS = 10000;
const CART_REQUEST_PER_QUEUED_REQUEST_MS = 5000;
const CART_REQUEST_MAX_TIMEOUT_MS = 40000;
// How long a timed-out request's identity is remembered so a late-arriving
// *_RESULT (the host finished it after all) can still be reconciled by the
// caller instead of being silently dropped, which is what left seats stuck
// as "in the cart but not selectable/removable" in #117787 / #117935.
const LATE_CART_RESULT_RETENTION_MS = 120000;

// requestId -> { resolve, reject, timeoutHandle }
const pendingCartRequests = new Map();

// requestId -> { type, expiryHandle } for requests whose promise already
// rejected on timeout, kept briefly so a late result can still be reconciled.
const timedOutCartRequests = new Map();

/**
 * Checks whether the given origin is present in the REACT_APP_ALLOWED_ORIGINS
 * env var (a comma-separated list). Returns false if the env var is unset.
 */
export function isOriginAllowed(origin) {
	const allowedOrigins = process.env.REACT_APP_ALLOWED_ORIGINS;

	if (!allowedOrigins) {
		return false;
	}

	const allowedOriginList = allowedOrigins.split(',').map((entry) => entry.trim());

	return allowedOriginList.includes(origin);
}

/**
 * Posts a single { type: 'WIDGET_READY' } message to window.parent.
 * targetOrigin is deliberately '*' - the widget doesn't know the host's
 * origin in advance.
 */
export function sendWidgetReady() {
	window.parent.postMessage({ type: MESSAGE_TYPES.WIDGET_READY }, '*');
}

/**
 * Attaches a one-shot 'message' listener that waits for a valid INIT message.
 *
 * - Messages from an origin not present in REACT_APP_ALLOWED_ORIGINS are
 *   silently discarded (a warning is logged, onOriginRejected is called if
 *   provided) and the listener stays attached, so a later valid INIT can
 *   still complete the handshake.
 * - Messages from an allowed origin whose payload isn't a valid INIT message
 *   are ignored and the listener stays attached.
 * - Once a valid INIT message is received from an allowed origin, onInit is
 *   called with the message payload and the message's origin, and the
 *   listener is removed.
 *
 * Returns a cleanup function that removes the listener (e.g. for use on
 * unmount if INIT never arrives).
 */
export function attachInitListener(onInit, onOriginRejected) {
	const handleMessage = (event) => {
		if (!isOriginAllowed(event.origin)) {
			console.warn(
				`postMessageHandshake: discarded message from disallowed origin "${event.origin}"`,
			);
			onOriginRejected?.(event.origin);
			return;
		}

		if (event.data?.type !== MESSAGE_TYPES.INIT) {
			return;
		}

		window.removeEventListener('message', handleMessage);
		onInit(event.data, event.origin);
	};

	window.addEventListener('message', handleMessage);

	return () => window.removeEventListener('message', handleMessage);
}

/**
 * Small local requestId generator - no uuid dependency exists in this repo
 * and none is being added for this. Not cryptographically unique, just
 * unique enough to match a request to its result within a single session.
 */
function generateRequestId() {
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Sends a cart-operation message (ADD_TO_CART / REMOVE_SEAT /
 * PATRON_HOLD_LOAD) to the host and returns a Promise that resolves with the
 * matching *_RESULT payload, rejects on the matching *_ERROR, or rejects after
 * a timeout (CART_REQUEST_BASE_TIMEOUT_MS, plus CART_REQUEST_PER_QUEUED_REQUEST_MS
 * for each request already outstanding, up to CART_REQUEST_MAX_TIMEOUT_MS), or
 * rejects immediately if targetOrigin is missing or '*' (wildcard targets are
 * never allowed for these sends).
 *
 * Requests are posted straight away, without waiting for earlier ones to be
 * answered. postMessage delivers them in order and the host handles them one
 * at a time in that order, so a burst of seat clicks reaches the host together
 * and the host can add the seats in a single cart call.
 *
 * attachCartResultListener() must be attached for the returned Promise to
 * ever resolve/reject via a host response.
 *
 * options.onRequestId, if provided, is called synchronously with this
 * request's requestId before the postMessage is sent, so a caller can
 * correlate a later reconciliation (e.g. a late attachCartResultListener
 * result) back to this specific request.
 */
function sendCartRequest(type, payload, targetOrigin, options = {}) {
	if (!targetOrigin || targetOrigin === '*') {
		return Promise.reject(
			new Error(
				`postMessageHandshake: refusing to send "${type}" with a missing or wildcard targetOrigin`,
			),
		);
	}

	const requestId = generateRequestId();
	const timeoutMs = Math.min(
		CART_REQUEST_BASE_TIMEOUT_MS + pendingCartRequests.size * CART_REQUEST_PER_QUEUED_REQUEST_MS,
		CART_REQUEST_MAX_TIMEOUT_MS,
	);

	options.onRequestId?.(requestId);

	return new Promise((resolve, reject) => {
		const timeoutHandle = setTimeout(() => {
			pendingCartRequests.delete(requestId);
			markCartRequestTimedOut(requestId, type);
			reject(new Error(`postMessageHandshake: "${type}" timed out waiting for a response`));
		}, timeoutMs);

		pendingCartRequests.set(requestId, { resolve, reject, timeoutHandle });

		window.parent.postMessage(
			{ type, requestId, payload },
			targetOrigin,
		);
	});
}

/** Test-only: forgets every outstanding and timed-out request so tests don't leak into each other's timeouts. */
export function resetCartRequests() {
	pendingCartRequests.forEach(({ timeoutHandle }) => clearTimeout(timeoutHandle));
	pendingCartRequests.clear();
	timedOutCartRequests.forEach(({ expiryHandle }) => clearTimeout(expiryHandle));
	timedOutCartRequests.clear();
}

function markCartRequestTimedOut(requestId, type) {
	const expiryHandle = setTimeout(() => {
		timedOutCartRequests.delete(requestId);
	}, LATE_CART_RESULT_RETENTION_MS);

	timedOutCartRequests.set(requestId, { type, expiryHandle });
}

/**
 * Sends an ADD_TO_CART postMessage to the host. See sendCartRequest.
 */
export function sendAddToCart(payload, targetOrigin, options) {
	return sendCartRequest(MESSAGE_TYPES.ADD_TO_CART, payload, targetOrigin, options);
}

/**
 * Sends a REMOVE_SEAT postMessage to the host. See sendCartRequest.
 */
export function sendRemoveSeat(payload, targetOrigin, options) {
	return sendCartRequest(MESSAGE_TYPES.REMOVE_SEAT, payload, targetOrigin, options);
}

/**
 * Sends a PATRON_HOLD_LOAD postMessage to the host. See sendCartRequest.
 */
export function sendPatronHoldLoad(payload, targetOrigin, options) {
	return sendCartRequest(MESSAGE_TYPES.PATRON_HOLD_LOAD, payload, targetOrigin, options);
}

/**
 * Attaches a persistent 'message' listener for ADD_TO_CART_RESULT /
 * REMOVE_SEAT_RESULT / PATRON_HOLD_LOAD_RESULT messages.
 *
 * - Messages from a disallowed origin are discarded (warning logged) and the
 *   listener stays attached, matching attachInitListener's behavior.
 * - On a valid result message from an allowed origin, resolves the pending
 *   request matching the message's requestId with the message's payload.
 * - On a valid *_ERROR message, rejects the matching pending request with the
 *   host's error message.
 * - If the requestId isn't pending but matches a request that already timed
 *   out (see CART_REQUEST_* above) and is still within its retention window,
 *   onLateResult(type, requestId, payload) is called instead - the host was
 *   genuinely still going to fulfil it, so the caller gets a chance to
 *   reconcile local state (e.g. mark a seat selected) rather than leaving it
 *   stuck the way #117787 / #117935 described.
 * - A late *_ERROR for a timed-out request is NOT a result: onLateResult is
 *   skipped and the optional onLateError(type, requestId, payload) is called
 *   instead, so the caller can drop any state kept for reconciliation.
 * - Any other unmatched requestId (unknown, or its retention window expired)
 *   is ignored.
 *
 * This listener is never removed on its own (unlike attachInitListener) -
 * cart operations can happen repeatedly for the lifetime of the widget.
 * Returns a cleanup function for use on unmount.
 */
export function attachCartResultListener(onLateResult, onLateError) {
	const handleMessage = (event) => {
		if (!isOriginAllowed(event.origin)) {
			console.warn(
				`postMessageHandshake: discarded message from disallowed origin "${event.origin}"`,
			);
			return;
		}

		const { type, requestId, payload } = event.data || {};

		const isResult = CART_RESULT_TYPES.includes(type);
		const isError = CART_ERROR_TYPES.includes(type);
		if (!isResult && !isError) {
			return;
		}

		const pending = pendingCartRequests.get(requestId);
		if (pending) {
			clearTimeout(pending.timeoutHandle);
			pendingCartRequests.delete(requestId);
			if (isError) {
				pending.reject(new Error(payload?.message || `postMessageHandshake: host failed "${type}"`));
				return;
			}
			pending.resolve(payload);
			return;
		}

		const timedOut = timedOutCartRequests.get(requestId);
		if (timedOut) {
			clearTimeout(timedOut.expiryHandle);
			timedOutCartRequests.delete(requestId);
			// A late *_ERROR means the host did NOT fulfil the request, so it must never reach onLateResult, which
			// reconciles as a success (e.g. marks the seat selected).
			if (isError) {
				onLateError?.(type, requestId, payload);
				return;
			}
			onLateResult?.(type, requestId, payload);
		}
	};

	window.addEventListener('message', handleMessage);

	return () => window.removeEventListener('message', handleMessage);
}

/**
 * Attaches a persistent 'message' listener for the host-initiated
 * SEAT_DESELECTED notification (one-way, no response expected).
 *
 * - Messages from a disallowed origin are discarded (warning logged) and the
 *   listener stays attached, matching attachInitListener's behavior.
 * - On a valid SEAT_DESELECTED message from an allowed origin, onSeatDeselected
 *   is called with the message's payload, every time (this listener is
 *   persistent, not one-shot).
 *
 * Returns a cleanup function for use on unmount.
 */
export function attachSeatDeselectedListener(onSeatDeselected) {
	const handleMessage = (event) => {
		if (!isOriginAllowed(event.origin)) {
			console.warn(
				`postMessageHandshake: discarded message from disallowed origin "${event.origin}"`,
			);
			return;
		}

		if (event.data?.type !== MESSAGE_TYPES.SEAT_DESELECTED) {
			return;
		}

		onSeatDeselected(event.data.payload);
	};

	window.addEventListener('message', handleMessage);

	return () => window.removeEventListener('message', handleMessage);
}
